import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import moment from 'moment';
import { removeEmpty } from 'utils';
import { Card } from 'components/UI';
import Box from '@mui/material/Box';
import Loading from 'components/Loading';
import Chip from '@mui/material/Chip';
import VersionStatus from './VersionStatus';
import Constants from 'constants/documents';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import VersionFileInput from './VersionFileInput';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentsActions } from 'state/modules/documents';
import { transformAsyncErrors } from 'utils/utils';
import VersionModal from 'components/Documents/VersionModal';

function WidgetHeader({ status, onChangeStatus, isDefault }) {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return isDefault ? (
    <Box
      flex={1}
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
    >
      <Typography sx={{ fontSize: 14, fontWeight: 500 }} variant="subtitle2">
        {i18n.t('documents.version_details')}
      </Typography>
      {status ? (
        <Button
          size="small"
          color="primary"
          onClick={handleClick}
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          }
          sx={{
            color: 'text.primary',
            marginRight: 1,
            fontWeight: 400,
            textTransform: 'none',
          }}
        >
          <VersionStatus status={status} defaultMessage={i18n.t('common.status')} />
        </Button>
      ) : null}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { boxShadow: '0 1px 3px rgba(0,0,0,.2)' },
        }}
      >
        <Box>
          {Constants.statuses.map((versionStatus) => (
            <MenuItem
              key={versionStatus}
              sx={{
                color: '#4D4F5C',
                fontSize: 14,
                padding: '8px 20px',
              }}
              onClick={() => {
                handleClose();
                onChangeStatus(versionStatus);
              }}
            >
              <VersionStatus status={versionStatus} />
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </Box>
  ) : (
    i18n.t('documents.version_details')
  );
}

function VersionDetailsWidget({
  tags,
  id,
  document,
  handleChange,
  handleRemoveFile,
  handleUploadFile,
  onViewDetails,
  onTagClick,
  showDetailsBtn,
  isUploading,
}) {
  const [asyncErrors, setAsyncErrors] = useState({});
  const [versionModal, setVersionModal] = useState(false);
  const { localeFormat } = useSelector((state) => state.organizations);
  const dispatch = useDispatch();
  const version = useSelector((state) => state.documents?.versions?.current || {});
  const fetchingVersion = useSelector((state) => state.documents?.fetchingDocument || false);
  const fetchedUploadFile = useSelector((state) => state.documents?.fetchedUploadFile || false);

  const file = useMemo(() => {
    if (version && version.files) {
      return version?.files[0];
    }
    return undefined;
  }, [version]);

  useEffect(() => {
    if (id) {
      dispatch(DocumentsActions.fetchVersionById(document, id));
    }
  }, [document, id, dispatch]);

  const onChangeAttr = (key, value) => {
    if (key === 'file') {
      return handleUploadFile(version.id, value);
    }
    return handleChange({ ...removeEmpty(version), [key]: value }, version.id);
  };

  const toggleVersionModal = () => {
    if (versionModal) {
      setAsyncErrors({});
    }
    setVersionModal(!versionModal);
  };

  const onEdit = async (data) => {
    const response = await dispatch(
      DocumentsActions.updateVersion(document, version?.id, data, null, true)
    );
    if (response.ok) {
      toggleVersionModal();
    } else {
      setAsyncErrors({
        ...asyncErrors,
        updateVersion: transformAsyncErrors(response?.data?.details),
      });
    }
  };

  const items = [
    {
      title: i18n.t('documents.edit_version'),
      can: { action: 'update', name: 'document_version' },
      onClick: toggleVersionModal,
    },
  ];

  return (
    <Card
      title={
        <WidgetHeader
          status={version?.status}
          isDefault={version.default}
          onChangeStatus={(status) => onChangeAttr('status', status)}
        />
      }
      menuItems={version?.default ? items : []}
      headerActionType="vertical"
      data-cy="versions-details-card"
    >
      {fetchingVersion && (
        <Box
          sx={{
            height: 150,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Loading />
        </Box>
      )}
      {Object.keys(version)?.length > 0 && !fetchingVersion && (
        <>
          <Box>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              {version.default || showDetailsBtn ? (
                <Box display="flex" flexDirection="row" alignItems="center">
                  <StarOutlinedIcon sx={{ color: '#FFB027', marginRight: 1 }} />
                  <Typography variant="subtitle2" sx={{ color: '#FFB027' }}>
                    {i18n.t('documents.default_version')}
                  </Typography>
                </Box>
              ) : null}
              {showDetailsBtn ? (
                <Button
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                  size="small"
                  color="primary"
                  onClick={() => onViewDetails(version.id)}
                >
                  {i18n.t('common.view_details')}
                </Button>
              ) : null}
            </Box>
            {version?.created_time && (
              <Box
                pb={1}
                pt={1}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ borderBottom: '1px solid #EEEEEE' }}
              >
                <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                  {i18n.t('documents.creation_date')}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 13, textAlign: 'right' }}>
                  {moment(version.created_time).format(localeFormat)}
                </Typography>
              </Box>
            )}
            <Box>
              <Box
                pb={1}
                pt={1}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ borderBottom: '1px solid #EEEEEE' }}
              >
                <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                  {`${i18n.t('common.name')}:`}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 13, textAlign: 'right' }}>
                  {version.version}
                </Typography>
              </Box>
              {version.expiration_time && (
                <Box
                  pb={1}
                  pt={1}
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ borderBottom: '1px solid #EEEEEE' }}
                >
                  <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                    {`${i18n.t('documents.expiration_date')}:`}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13, textAlign: 'right' }}>
                    {moment(version.expiration_time).format(localeFormat)}
                  </Typography>
                </Box>
              )}
              {version.description && (
                <Box
                  pb={1}
                  pt={1}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="left"
                  sx={{ borderBottom: '1px solid #EEEEEE' }}
                >
                  <Typography variant="subtitle2" sx={{ fontSize: 13, width: '100%' }}>
                    {`${i18n.t('common.description')}:`}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13, marginTop: 1, width: '100%' }}>
                    {version.description}
                  </Typography>
                </Box>
              )}
              <Box mt={2} pb={1} pt={1} display="flex" flexDirection="column">
                <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                  {`${i18n.t('documents.file')}:`}
                </Typography>
                <Box display="flex">
                  <VersionFileInput
                    file={file}
                    fetchedUploadFile={fetchedUploadFile}
                    name="file"
                    onChange={onChangeAttr}
                    isUploading={isUploading}
                    onRemoveFile={() => handleRemoveFile(version.id)}
                  />
                </Box>
              </Box>
              {tags.length ? (
                <Box
                  mt={2}
                  pb={1}
                  pt={1}
                  display="flex"
                  flexDirection="row"
                  alignItems="flex-start"
                >
                  <Typography variant="subtitle2" sx={{ marginTop: 1 }}>
                    {`${i18n.t('documents.tags')}:`}
                  </Typography>
                  <Box sx={{ flexWrap: 'wrap', display: 'flex', ml: 0.5 }}>
                    {tags.map(({ label }) => (
                      <Chip
                        key={label}
                        label={label}
                        clickable
                        color="primary"
                        onClick={() => onTagClick(label)}
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Box>
        </>
      )}
      {Object.keys(version)?.length === 0 && !fetchingVersion && (
        <Typography>
          <Box fontSize={13} component="span" sx={{ color: 'text.secondary' }}>
            {i18n.t(`documents.empty_versions`)}
          </Box>
        </Typography>
      )}
      {versionModal && (
        <VersionModal
          mode={'update'}
          open={versionModal}
          handleCreate={onEdit}
          handleClose={toggleVersionModal}
          asyncErrors={asyncErrors.updateVersion}
          {...version}
        />
      )}
    </Card>
  );
}

WidgetHeader.propTypes = {
  status: PropTypes.string,
  onChangeStatus: PropTypes.func.isRequired,
  isDefault: PropTypes.bool,
};

WidgetHeader.defaultProps = {
  status: undefined,
  isDefault: false,
};

VersionDetailsWidget.propTypes = {
  /**
   * Tags to be applied, would contain the linked objects to this document or version
   */
  tags: PropTypes.array,
  /**
   * Version of a document
   */
  version: PropTypes.any,
  /**
   * Changes handler for widget's values (status, file)
   */
  handleChange: PropTypes.func,
  /**
   * Handler for view details button
   */
  onViewDetails: PropTypes.func,
  /**
   * Handler for tags chips click
   */
  onTagClick: PropTypes.func,
  /**
   * Boolean to show details button
   */
  showDetailsBtn: PropTypes.bool,
  /**
   * Document id
   */
  document: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /**
   * Version id
   */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /**
   * Handler for removing a file
   */
  handleRemoveFile: PropTypes.func.isRequired,
  /**
   * Handler for uploading a file
   */
  handleUploadFile: PropTypes.func.isRequired,
  /**
   * Boolean for upload state
   */
  isUploading: PropTypes.bool,
};

VersionDetailsWidget.defaultProps = {
  tags: [],
  version: {},
  handleCreate: undefined,
  onViewDetails: undefined,
  onTagClick: undefined,
  showDetailsBtn: true,
  isUploading: false,
};

export default VersionDetailsWidget;
